import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import '../components/css/Home.css';
import axios from 'axios';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";


const Home = () => {
  const [marketStats, setMarketStats] = useState(null);
  const [luncRank, setLuncRank] = useState(null);
  const [activeVolumeTab, setActiveVolumeTab] = useState('24h');
  const [volumeStats, setVolumeStats] = useState(null);
  const [offchain, setOffchain] = useState({ lunc_volume: 0, lunc_value: 0, ustc_volume: 0, ustc_value: 0 });
  const [blockHeight, setBlockHeight] = useState({ chain: '000' });
  const [networkHealth, setNetworkHealth] = useState({ validatorCount: 0, status: 'Loading...', proposalsOpen: 0 });
  const [poolStats, setPoolStats] = useState(null);
  const [price, setPrice] = useState(null); 
  const [binanceBurnData, setBinanceBurnData] = useState(null);
  const [binanceBurnError, setBinanceBurnError] = useState('');
  const [showBinanceBurnHistory, setShowBinanceBurnHistory] = useState(false);
  const [totalCoins, setTotalCoins] = useState(null);
  const [ustcTotalSupply, setUstcTotalSupply] = useState(null);
  const [ustcMarketCap, setUstcMarketCap] = useState(null);
  const [depositProposals, setDepositProposals] = useState([]);
  const [liveProposals, setLiveProposals] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [chartDetails, setChartDetails] = useState(null);
  const [showUSTCHistory, setShowUSTCHistory] = useState(false);
  const [ustcChartData, setUstcChartData] = useState([]);
  const [ustcChartDetails, setUstcChartDetails] = useState(null);



const fetchHistory = async () => {
  try {
    const res = await axios.get("/api/lunc/history");
    setChartData(res.data.dataPoints || []);
    setChartDetails(res.data);
    setShowHistory(true);
  } catch (err) {
    console.error("❌ Error fetching chart history:", err.message);
  }
};

const fetchUSTCHistory = async () => {
  try {
    const res = await axios.get("/api/ustc/history");
    setUstcChartData(res.data.dataPoints || []);
    setUstcChartDetails(res.data);
    setShowUSTCHistory(true);
  } catch (err) {
    console.error("❌ Error fetching USTC chart history:", err.message);
  }
};



useEffect(() => {
  const fetchPrice = async () => {
    try {
      const res = await axios.get('https://api.binance.com/api/v3/ticker/price?symbol=LUNCUSDT');
      setPrice(parseFloat(res.data.price));
    } catch (err) {
      console.error('❌ Error fetching LUNC price:', err);
    }
  };
  fetchPrice();
  const interval = setInterval(fetchPrice, 60000);
  return () => clearInterval(interval);
}, []);

useEffect(() => {
  const fetchBinanceBurns = async () => {
    try {
      const res = await axios.get('/api/lunc/binance-burns');
      setBinanceBurnData(res.data);
      setBinanceBurnError('');
    } catch (err) {
      console.error('❌ Error fetching Binance burns:', err);
      setBinanceBurnError('Unable to load Binance burns');
    }
  };

  fetchBinanceBurns();
  const interval = setInterval(fetchBinanceBurns, 60 * 60 * 1000);
  return () => clearInterval(interval);
}, []);


  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get('/api/validators/lunc-market-stats');
        setMarketStats(res.data);
      } catch (err) {
        console.error('Error fetching market stats:', err);
      }
    };
    fetchStats();
  }, []);

useEffect(() => {
  const fetchRankAndTotal = async () => {
    try {
      const res = await axios.get('/api/lunc/rank-and-total');
      setLuncRank(res.data.rank);
      setTotalCoins(res.data.total);
    } catch (err) {
      console.error('Error fetching LUNC rank and total:', err);
    }
  };
  fetchRankAndTotal();
}, []);

  useEffect(() => {
    const fetchVolume = async () => {
      try {
        const res = await axios.get('/api/lunc/volume');
        setVolumeStats(res.data);
      } catch (err) {
        console.error('❌ Volume fetch failed:', err.message);
      }
    };
    fetchVolume();
  }, []);

  useEffect(() => {
    const fetchOffchain = async () => {
      try {
        const res = await axios.get('/api/volume/offchain');
        setOffchain(res.data);
      } catch (err) {
        console.error('❌ Off-chain fetch failed:', err.message);
      }
    };
    fetchOffchain();
    const interval = setInterval(fetchOffchain, 600000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchChainHeight = async () => {
      try {
        const res = await axios.get('/api/chain-height1');
        const chain = parseInt(res.data?.height || 0, 10);
        setBlockHeight({ chain });
      } catch (err) {
        console.error('Error fetching chain height:', err.message);
        setBlockHeight({ chain: '00' });
      }
    };
    fetchChainHeight();
    const interval = setInterval(fetchChainHeight, 6000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchHealth = async () => {
      try {
        const res = await axios.get('/api/lunc/network-health');
        setNetworkHealth(res.data);
      } catch (err) {
        console.error('Error fetching network health:', err);
      }
    };
    fetchHealth();
  }, []);

  useEffect(() => {
    const fetchPools = async () => {
      try {
        const res = await axios.get('/api/lunc/pools');
        setPoolStats(res.data);
      } catch (err) {
        console.error('Error fetching pool stats:', err);
      }
    };
    fetchPools();
  }, []);


const totalValue = binanceBurnData?.totalValueUsd != null
  ? `$${binanceBurnData.totalValueUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
  : price && binanceBurnData?.totalLuncBurned
    ? `$${(binanceBurnData.totalLuncBurned * price).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    : '...';



useEffect(() => {
  const fetchUSTCTotal = async () => {
    try {
      const res = await axios.get('/api/ustc/total');
      const total = res.data.total;
      setUstcTotalSupply(total);

      // 🧮 If marketStats is already fetched, calculate
      if (marketStats?.ustcPrice && total) {
        const marketCap = total * marketStats.ustcPrice;
        setUstcMarketCap(marketCap);
      }
    } catch (err) {
      console.error('Failed to fetch USTC total supply:', err);
    }
  };

  fetchUSTCTotal();
}, [marketStats]);
 




function mapStatusToPhase(status) {
  switch (status) {
    case 'deposit_period':
    case 'PROPOSAL_STATUS_DEPOSIT_PERIOD':
    case 1:
      return 'Deposit Phase';
    case 'voting_period':
    case 'PROPOSAL_STATUS_VOTING_PERIOD':
    case 2:
      return 'Live';
    case 'PROPOSAL_STATUS_PASSED':
    case 3:
      return 'Passed';
    case 'PROPOSAL_STATUS_REJECTED':
    case 'PROPOSAL_STATUS_FAILED':
    case 4:
      return 'Failed';
    default:
      return 'Unknown';
  }
}

useEffect(() => {
  async function fetchLiveProposals() {
    try {
      const res = await axios.get('/api/lunc/governance-proposal/live');
      const arr = res.data.proposals || [];
      // ensure every proposal has a human-friendly phase
      arr.forEach(p => {
        if (!p.phase) p.phase = mapStatusToPhase(p.status);
      });
      setLiveProposals(arr);
    } catch (err) {
      console.error('❌ Failed to fetch live proposals:', err);
      setLiveProposals([]); 
    }
  }
  fetchLiveProposals();
}, []);



useEffect(() => {
  async function fetchDepositProposals() {
    try {
      const res = await axios.get('/api/lunc/governance-proposals/deposit');
      const arr = res.data.proposals || [];
      arr.forEach(p => {
        if (!p.phase) p.phase = 'Deposit Phase';
      });
      setDepositProposals(arr);
    } catch (err) {
      console.error('❌ Failed to fetch deposit proposals:', err);
      setDepositProposals([]);
    }
  }
  fetchDepositProposals();
}, []);


// 🔹 Determine if the proposal is passing based on live votes
// 🔹 How much % or LUNC is needed to reach quorum
function getQuorumGap(p) {
  const turnout = Number(p.turnout_percent || 0);
  const quorumTarget = 40; // quorum target (%)
  const bonded = Number(p.bonded_total || 0); // total bonded LUNC
  const quorumLunc = (bonded * quorumTarget) / 100; // target quorum in LUNC
  const currentTurnoutLunc = (bonded * turnout) / 100; // current turnout in LUNC
  const remainingLunc = quorumLunc - currentTurnoutLunc;
  const gap = quorumTarget - turnout;

  // ✅ Already reached
  if (gap <= 0) {
    return `✅ Quorum reached (${turnout.toFixed(2)}%)`;
  }

  // 🔹 Format numbers for readability (e.g., 12.5B)
  const shortFmt = (val) => {
    if (val >= 1_000_000_000) return `${(val / 1_000_000_000).toFixed(2)}B`;
    if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(2)}M`;
    if (val >= 1_000) return `${(val / 1_000).toFixed(2)}K`;
    return val.toLocaleString();
  };

  // 🕓 Show both percentage and LUNC short amount
  return `🕓 ${gap.toFixed(2)}% more turnout needed (${shortFmt(remainingLunc)} LUNC)`;
}


// 🔹 How far ahead or behind the proposal is (vote margin)
function getVoteMargin(p) {
  const yes = Number(p.yes_count || 0);
  const no = Number(p.no_count || 0);
  const veto = Number(p.veto_count || 0);
  const total = yes + no + veto;
  if (total === 0) return "";

  const yesPct = (yes / total) * 100;
  const againstPct = ((no + veto) / total) * 100;
  const margin = yesPct - againstPct;

  if (margin > 0) return `🟢 Passing by ${margin.toFixed(2)}%`;
  if (margin < 0) return `🔴 Failing by ${Math.abs(margin).toFixed(2)}%`;
  return "⚖️ Evenly split";
}




// 🔹 Label used beside “Live” (e.g., Live Passing / Live Failing)
function getPhaseLabel(p) {
  if (p.phase === 'Live') {
    return isProposalPassing(p) ? 'Passing' : 'Failing';
  }
  return p.phase;
}




function getPhaseClass(p) {
  if (p.phase === "Live") {
    // Reuse the exact same pass/fail logic as isProposalPassing()
    return isProposalPassing(p) ? "proposal-pass" : "proposal-fail";
  }

  if (p.phase === "Passed") return "proposal-pass";
  if (p.phase === "Failed" || p.phase === "Rejected") return "proposal-fail";
  if (p.phase === "Deposit Phase") return "proposal-deposit";

  return "";
}





// ✅ must be defined inside Home component, before it's used
function isProposalPassing(p) {
  if (!p) return false;

  const yes = Number(p.yes_count || 0);
  const no = Number(p.no_count || 0);
  const veto = Number(p.veto_count || 0);
  const abstain = Number(p.abstain_count || 0);
  const total = yes + no + veto + abstain;

  if (total === 0) return false;

  const yesPct = (yes / total) * 100;
  const noPct = (no / total) * 100;
  const vetoPct = (veto / total) * 100;

  // Passing only if YES > (NO + VETO) and quorum ≥ 40%
  return yesPct > (noPct + vetoPct) && (p.turnout_percent || 0) >= 40;
}

function formatPoolNumber(value, fallback = 'Loading...', options) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return fallback;
  return numericValue.toLocaleString(undefined, options);
}

function formatDifference(a, b, fallback = '...', options) {
  const result = Number(a) - Number(b);
  if (!Number.isFinite(result)) return fallback;
  return result.toLocaleString(undefined, options);
}

function formatProduct(a, b, fallback = '...', options) {
  const result = Number(a) * Number(b);
  if (!Number.isFinite(result)) return fallback;
  return result.toLocaleString(undefined, options);
}

function formatDate(value) {
  if (!value) return 'Loading...';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-GB');
}

function formatNumber(value, fallback = 'Loading...', options = {}) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return fallback;
  return numericValue.toLocaleString(undefined, options);
}

function formatCurrency(value, fallback = '...') {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return fallback;
  return `$${numericValue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}



return (
    <div className="page-container">

      {/* 1. Hero Section */}
   
 <section className="home-section hero-section">
  <div className="hero-copy">
    <p className="hero-eyebrow">Luna Classic network intelligence</p>
    <h1>Cosmos-Station</h1>
    <p className="hero-subtitle">Your gateway to the LUNA Classic blockchain</p>
    <p className="hero-rank">
      LUNC Market Rank on CMC:{' '}
      {luncRank != null && Number.isFinite(Number(totalCoins))
        ? `#${luncRank} out of ${totalCoins.toLocaleString()}`
        : 'Loading...'}
    </p>
    <div className="hero-actions">
      <Link to="/Services" className="hero-button primary">Explore validators</Link>
      <Link to="/valservices" className="hero-button secondary">Node services</Link>
    </div>
  </div>
  <div className="hero-visual">
    <div className="central-logo-wrapper">
      <span className="central-logo-orbit central-logo-orbit-outer" aria-hidden="true" />
      <span className="central-logo-orbit central-logo-orbit-inner" aria-hidden="true" />
      <img src="/cosmos-station-mark.svg" alt="Cosmos-Station logo" className="central-logo" />
    </div>
  </div>
</section>
<section className="home-section price-stats-section">
<div className="stat-grid two-col-burns">
  <div
    className="stat-card burn-card"
    role="button"
    tabIndex={0}
    onClick={() => setShowBinanceBurnHistory(true)}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setShowBinanceBurnHistory(true);
      }
    }}
  >
    <h2 style={{ marginBottom: '8px' }}>🔥 Latest Binance Burn</h2>
    <p><strong>Date:</strong> {formatDate(binanceBurnData?.latest?.date)}</p>
    <p><strong>Amount:</strong> {formatNumber(binanceBurnData?.latest?.amount)} LUNC</p>
    <p className="burn-card-hint">Click to view major Binance burns</p>
  </div>

  <div className="stat-card">
    <h2 style={{ marginBottom: '8px' }}>🔥 Total Burned</h2>
    <p><strong>Approx Amount:</strong> {formatNumber(binanceBurnData?.totalLuncBurned)} LUNC</p>
    <p><strong>💵 Approx Value:</strong> <span style={{ color: 'limegreen' }}>{totalValue}</span></p>
    {binanceBurnData?.lastUpdated && (
      <p className="burn-card-hint">Updated: {binanceBurnData.lastUpdated}</p>
    )}
  </div>
</div>

<div className="thank-you-box">
  🙏 <strong>Thank you Binance</strong>
  {binanceBurnError && <span className="burn-error"> {binanceBurnError}</span>}
</div>

{showBinanceBurnHistory && (
  <div className="chart-modal-overlay" onClick={() => setShowBinanceBurnHistory(false)}>
    <div className="chart-modal binance-burn-modal" onClick={(e) => e.stopPropagation()}>
      <button className="close-btn" onClick={() => setShowBinanceBurnHistory(false)}>×</button>
      <h2>Major Binance Burn History</h2>
      <div className="burn-summary-grid">
        <div>
          <strong>Total LUNC Burned</strong>
          <span>{formatNumber(binanceBurnData?.totalLuncBurned)} LUNC</span>
        </div>
        <div>
          <strong>Latest LUNC Burn</strong>
          <span>{formatNumber(binanceBurnData?.latest?.amount)} LUNC</span>
        </div>
        <div>
          <strong>Burns shown</strong>
          <span>
            {formatNumber(binanceBurnData?.majorBurnCount, '0')} burns at or above{' '}
            {formatNumber(binanceBurnData?.majorBurnMinimumLunc, '100,000,000')} LUNC
          </span>
        </div>
        <div>
          <strong>Tracked Binance wallet</strong>
          <span>{binanceBurnData?.trackedWallet || 'Loading...'}</span>
        </div>
        <div>
          <strong>Official LUNC burn wallet</strong>
          <span>{binanceBurnData?.officialBurnWallet || 'Loading...'}</span>
        </div>
      </div>

      <div className="burn-source-row">
        {binanceBurnData?.source && (
          <a href={binanceBurnData.source} target="_blank" rel="noopener noreferrer">LUNC Metrics source</a>
        )}
        {binanceBurnData?.binanceRecords && (
          <a href={binanceBurnData.binanceRecords} target="_blank" rel="noopener noreferrer">Binance burn records</a>
        )}
      </div>

      <div className="burn-history-table-wrap">
        <table className="burn-history-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Coin</th>
              <th>Amount</th>
              <th>Value</th>
              <th>Block</th>
              <th>TX</th>
            </tr>
          </thead>
          <tbody>
            {(binanceBurnData?.burns || []).map((burn) => (
              <tr key={`${burn.tx_hash}-${burn.blockHeight}`}>
                <td>{formatDate(burn.date)}</td>
                <td>{burn.coin}</td>
                <td>{formatNumber(burn.amount, '...', { maximumFractionDigits: 6 })}</td>
                <td>{burn.value_usd ? formatCurrency(burn.value_usd) : '—'}</td>
                <td>{burn.blockHeight?.toLocaleString?.() || burn.blockHeight}</td>
                <td>
                  {burn.tx_url ? (
                    <a href={burn.tx_url} target="_blank" rel="noopener noreferrer">View</a>
                  ) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
)}
</section>
<section className="home-section price-stats-section">
  <h2>LUNC Market Stats</h2>
  <div className="stat-grid">


 {/* 🟢 LUNC PRICE WITH POPUP CHART */}
<div
  className="stat-card"
  onClick={fetchHistory}   // ✅ fetch data when clicked
  style={{ cursor: "pointer" }}
>
  <strong>LUNC Price:</strong>{" "}
  ${marketStats?.luncPrice?.toFixed(8) ?? "Loading..."}
  <p style={{ fontSize: "0.9rem", color: "#00c49f" }}>
  </p>
</div>


{showHistory && (
  <div className="chart-modal-overlay" onClick={() => setShowHistory(false)}>
    <div className="chart-modal" onClick={(e) => e.stopPropagation()}>
      <button className="close-btn" onClick={() => setShowHistory(false)}>×</button>

      <h2 style={{ color: "#00c49f" }}>LUNC Market Details</h2>

      {/* ✅ Detailed price info from chartDetails */}
      {chartDetails ? (
        <div className="price-details">
          <p><strong>Current Price:</strong> ${chartDetails.price?.toFixed(8)}</p>
          <p><strong>24h High:</strong> ${chartDetails.high?.toFixed(8)}</p>
          <p><strong>24h Low:</strong> ${chartDetails.low?.toFixed(8)}</p>
          <p><strong>24h Change:</strong> {chartDetails.changePercent?.toFixed(2)}%</p>
          <p><strong>24h Volume:</strong> {chartDetails.volume?.toLocaleString()} LUNC</p>
          <p><strong>24h Value:</strong> ${chartDetails.quoteVolume?.toLocaleString()}</p>
        </div>
      ) : (
        <p>Loading chart data...</p>
      )}

      {/* ✅ 90-day chart */}
      <h3 style={{ marginTop: "20px" }}>90-Day Price History</h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="time" />
          <YAxis domain={["auto", "auto"]} />
          <Tooltip />
          <Line type="monotone" dataKey="close" stroke="#00c49f" dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>
)}




    <div className="stat-card">
      Total Supply: {formatPoolNumber(marketStats?.circulatingSupply, '...')} LUNC
    </div>

   <div className="stat-card">
  Circulating Supply: {formatDifference(marketStats?.circulatingSupply, marketStats?.totalStaked)} LUNC
</div>

<div className="stat-card">
  Market Cap: {marketStats?.circulatingSupply && marketStats?.totalStaked && marketStats?.luncPrice
    ? `$${formatProduct(
        Number(marketStats.circulatingSupply) - Number(marketStats.totalStaked),
        marketStats.luncPrice,
        '...',
        { maximumFractionDigits: 0 }
      )}`
    : '...'}
     <br />
      <small>
     (Total Supply Cap: {marketStats?.circulatingSupply && marketStats?.luncPrice
      ? `$${formatProduct(marketStats.circulatingSupply, marketStats.luncPrice, '...', { maximumFractionDigits: 0 })}`
      : '...'})
     </small>
    </div>
      <div className="stat-card">
      Total Staked: {formatPoolNumber(marketStats?.totalStaked, '...')} LUNC
     </div>
  
  </div>
</section>


<section className="home-section price-stats-section">
  <h2>USTC Market Stats</h2>

  <div className="stat-grid">
   <div
  className="stat-card"
  onClick={fetchUSTCHistory}
  style={{ cursor: "pointer" }}
>
  <strong>USTC Price:</strong>{" "}
  ${marketStats?.ustcPrice?.toFixed(4) ?? "Loading..."}
</div>

{showUSTCHistory && (
  <div
    className="chart-modal-overlay"
    onClick={() => setShowUSTCHistory(false)}
  >
    <div className="chart-modal" onClick={(e) => e.stopPropagation()}>
      <button
        className="close-btn"
        onClick={() => setShowUSTCHistory(false)}
      >
        ×
      </button>

      <h2 style={{ color: "#00ffc8" }}>USTC Market Details</h2>

      {ustcChartDetails ? (
        <div className="price-details">
          <p>
            <strong>Current Price:</strong>{" "}
            ${ustcChartDetails.price?.toFixed(6)}
          </p>
          <p>
            <strong>24h High:</strong>{" "}
            ${ustcChartDetails.high?.toFixed(6)}
          </p>
          <p>
            <strong>24h Low:</strong>{" "}
            ${ustcChartDetails.low?.toFixed(6)}
          </p>
          <p>
            <strong>24h Change:</strong>{" "}
            {ustcChartDetails.changePercent?.toFixed(2)}%
          </p>
          <p>
            <strong>24h Volume:</strong>{" "}
            {ustcChartDetails.volume?.toLocaleString()} USTC
          </p>
          <p>
            <strong>24h Value:</strong>{" "}
            ${ustcChartDetails.quoteVolume?.toLocaleString()}
          </p>
        </div>
      ) : (
        <p>Loading chart data...</p>
      )}

      <h3 style={{ marginTop: "20px" }}>90-Day Price History</h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={ustcChartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="time" />
          <YAxis domain={["auto", "auto"]} />
          <Tooltip />
          <Line
            type="monotone"
            dataKey="close"
            stroke="#00ffc8"
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>
)}


    <div className="stat-card">
      Total Supply (USTC): {ustcTotalSupply ? ustcTotalSupply.toLocaleString() : 'Loading...'} USTC
    </div>

    <div className="stat-card">
      Market Cap (USTC): {ustcMarketCap ? `$${ustcMarketCap.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : 'Loading...'}
    </div>
  </div>
</section>

<section className="home-section price-stats-section">
  <h2>Community & Oracle Pools</h2>
  <div className="stat-grid">
    <div className="stat-card">
      <strong>Community Pool (LUNC):</strong><br />
      {`${formatPoolNumber(poolStats?.community?.lunc)} LUNC`}
    </div>
    <div className="stat-card">
      <strong>Value (LUNC):</strong><br />
      {`$${formatPoolNumber(poolStats?.community?.lunc_usd, '...', { maximumFractionDigits: 2 })}`}
    </div>
    <div className="stat-card">
      <strong>Community Pool (USTC):</strong><br />
      {`${formatPoolNumber(poolStats?.community?.ustc)} USTC`}
    </div>
    <div className="stat-card">
      <strong>Value (USTC):</strong><br />
      {`$${formatPoolNumber(poolStats?.community?.ustc_usd, '...', { maximumFractionDigits: 2 })}`}
    </div>
  </div>

  <div className="stat-grid">
    <div className="stat-card">
      <strong>Oracle Pool (LUNC):</strong><br />
      {`${formatPoolNumber(poolStats?.oracle?.lunc)} LUNC`}
    </div>
    <div className="stat-card">
      <strong>Value (LUNC):</strong><br />
      {`$${formatPoolNumber(poolStats?.oracle?.lunc_usd, '...', { maximumFractionDigits: 2 })}`}
    </div>
    <div className="stat-card">
      <strong>Oracle Pool (USTC):</strong><br />
      {`${formatPoolNumber(poolStats?.oracle?.ustc)} USTC`}
    </div>
    <div className="stat-card">
      <strong>Value (USTC):</strong><br />
      {`$${formatPoolNumber(poolStats?.oracle?.ustc_usd, '...', { maximumFractionDigits: 2 })}`}
    </div>
  </div>
</section>

      {/* 2. Price Stats */}
      
<section className="home-section price-stats-section">
  <h2>LUNC & USTC Taxed On-Chain Volume</h2>

  {/* Timeframe Tabs */}
  <div className="volume-tabs">
    {['24h', '7d', '30d', 'all'].map((label) => (
      <button
        key={label}
        className={activeVolumeTab === label ? 'active' : ''}
        onClick={() => setActiveVolumeTab(label)}
      >
        {label}
      </button>
    ))}
  </div>

  <div className="stat-grid">
    {/* 🔹 LUNC Volume */}
    <div className="stat-card">
      <strong>{activeVolumeTab} Volume (LUNC):</strong><br />
      {volumeStats ? (
        <>
          {(parseFloat(volumeStats[activeVolumeTab === '24h' ? 'lunc' : `lunc_${activeVolumeTab}`] || 0) / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 })} LUNC
          <br />
          <span style={{ color: 'orangered' }}>
            🔥 Burned: {(parseFloat(volumeStats?.burnt?.lunc?.[activeVolumeTab] || 0) / 1_000_000).toLocaleString(undefined, { maximumFractionDigits: 2 })} LUNC
          </span>
        </>
      ) : 'Loading...'}
    </div>

    {/* 💰 LUNC USD Value */}
    <div className="stat-card">
      <strong>Value (LUNC):</strong><br />
      {volumeStats ? (
        <>
          ${(
            (parseFloat(volumeStats[activeVolumeTab === '24h' ? 'lunc' : `lunc_${activeVolumeTab}`] || 0) / 1_000_000) *
            (parseFloat(volumeStats?.lunc_price || 0))
          ).toLocaleString(undefined, { maximumFractionDigits: 2 })}
          <br />
          <span style={{ color: 'orangered' }}>
            🔥 Burned: $
            {(
              (parseFloat(volumeStats?.burnt?.lunc?.[activeVolumeTab] || 0) / 1_000_000) *
              (parseFloat(volumeStats?.lunc_price || 0))
            ).toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </span>
        </>
      ) : 'Loading...'}
    </div>

    {/* 🔹 USTC Volume */}
    <div className="stat-card">
      <strong>{activeVolumeTab} Volume (USTC):</strong><br />
      {volumeStats ? (
        <>
          {parseFloat(volumeStats[activeVolumeTab === '24h' ? 'ustc' : `ustc_${activeVolumeTab}`] || 0).toLocaleString()} USTC
          <br />
          <span style={{ color: 'orangered' }}>
            🔥 Burned: {parseFloat(volumeStats?.burnt?.ustc?.[activeVolumeTab] || 0).toLocaleString()} USTC
          </span>
        </>
      ) : 'Loading...'}
    </div>

    {/* 💰 USTC USD Value */}
    <div className="stat-card">
      <strong>Value (USTC):</strong><br />
      {volumeStats ? (
        <>
          ${parseFloat(volumeStats[activeVolumeTab === '24h' ? 'ustc_usd' : `ustc_${activeVolumeTab}_usd`] || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
          <br />
          <span style={{ color: 'orangered' }}>
            🔥 Burned: $
            {parseFloat(volumeStats?.burnt_usd?.ustc?.[activeVolumeTab] || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </span>
        </>
      ) : 'Loading...'}
    </div>
  </div>

  {/* ⏱️ Time range footer */}
  {volumeStats?.time_ranges?.[activeVolumeTab] && (
    <div className="volume-range">
      Showing data from <strong>{new Date(volumeStats.time_ranges[activeVolumeTab].from).toLocaleDateString('en-GB')}</strong>
      to <strong>{new Date(volumeStats.time_ranges[activeVolumeTab].to).toLocaleDateString('en-GB')}</strong>
    </div>
  )}
</section>




<section className="home-section price-stats-section">
  <h2>Off Chain</h2>
<div className="stat-grid">
<div className="stat-card">
  <h4>Off-chain Volume (LUNC):</h4>
  <p>{offchain.lunc_volume ? offchain.lunc_volume.toLocaleString() + ' LUNC' : 'Loading...'}</p>
</div>
<div className="stat-card">
  <h4>Value (LUNC):</h4>
  <p>{offchain.lunc_value ? '$' + offchain.lunc_value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '$...'}</p>
</div>

<div className="stat-card">
  <h4>Off-chain Volume (USTC):</h4>
  <p>{offchain.ustc_volume ? offchain.ustc_volume.toLocaleString() + ' USTC' : 'Loading...'}</p>
</div>
<div className="stat-card">
  <h4>Value (USTC):</h4>
  <p>{offchain.ustc_value ? '$' + offchain.ustc_value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '$...'}</p>
</div>

</div>

</section>
<section className="home-section network-metrics-section">
  <h2>Network Health</h2>
  <div className="stat-grid">
    <div className="stat-card">
      <strong>Chain Height:</strong><br />
      {blockHeight.chain.toLocaleString()}
    </div>
    <div className="stat-card">
      <strong>Validators:</strong><br />
      {networkHealth.validatorCount}
    </div>
    <div className="stat-card">
      <strong>Blockchain Status:</strong><br />
      {networkHealth.status}
    </div>
    <div className="stat-card">
      <strong>Proposals Live:</strong><br />
      {networkHealth.proposalsOpen}
    </div>
  </div>
</section>




{/* 🔹 Live Proposals Section */}
<section className="home-section news-section">
  <h2>Live Proposals</h2>

  {!liveProposals ? (
    <p>Loading proposals…</p>
  ) : liveProposals.length === 0 ? (
    <p>No live proposals right now.</p>
  ) : (
    <div className="news-grid">
      {liveProposals.map((p) => (
        <div key={p.id} className={`news-card ${getPhaseClass(p)}`}>
          <p className={`proposal-phase live ${isProposalPassing(p) ? 'pass' : 'fail'}`}>
            Live {isProposalPassing(p) ? 'Passing' : 'Failing'}
          </p>

          <h3 className="proposal-id">
            <span style={{ color: '#00ffc8' }}>Proposal #{p.id}</span>
          </h3>

          <p className="proposal-title">{p.title}</p>

          {/* 🔹 Bigger, clearer URL display */}
          {p.url ? (
            <p className="proposal-url">
              <a
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: '1.05rem',
                  fontWeight: '500',
                  color: isProposalPassing(p) ? '#00ff9c' : '#ff3366',
                  textDecoration: 'underline',
                  wordBreak: 'break-word',
                }}
              >
                {p.url}
              </a>
            </p>
          ) : p.summary ? (
            <p className="proposal-summary">{p.summary}</p>
          ) : null}

          <p className="news-date">
            Voting ends on{' '}
            {p.voting_end_time
              ? new Date(p.voting_end_time).toLocaleDateString('en-GB')
              : 'Unknown'}
          </p>

          {/* 🧮 Helper: Shorten big LUNC numbers */}
          {(() => {
            const formatLunc = (val) => {
              const n = Number(val);
              if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2)}B`;
              if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
              if (n >= 1_000) return `${(n / 1_000).toFixed(2)}K`;
              return n.toLocaleString();
            };

            return (
              <ul className="proposal-tally">
                <li>
                  ✅ Yes: {formatLunc(p.yes_count)} LUNC (
                  <strong>{p.yes_percent ?? 0}%</strong>)
                </li>
                <li>
                  ❌ No: {formatLunc(p.no_count)} LUNC (
                  <strong>{p.no_percent ?? 0}%</strong>)
                </li>
                <li>
                  ⚠️ Abstain: {formatLunc(p.abstain_count)} LUNC (
                  <strong>{p.abstain_percent ?? 0}%</strong>)
                </li>
                <li>
                  🚫 Veto: {formatLunc(p.veto_count)} LUNC (
                  <strong>{p.veto_percent ?? 0}%</strong>)
                </li>
              </ul>
            );
          })()}

          <p className="proposal-detail">
            Turnout: {p.turnout_percent ?? 0}% of staked LUNC •{' '}
            Quorum: {p.quorum_threshold?.toLocaleString() ?? 'N/A'} LUNC (
            {p.bonded_total
              ? ((p.quorum_threshold / p.bonded_total) * 100).toFixed(2)
              : '0.00'}
            %)
            <br />
            {p ? getQuorumGap(p) : ''}
            <br />
            {p ? getVoteMargin(p) : ''}
          </p>
        </div>
      ))}
    </div>
  )}
</section>








<section className="home-section news-section">
  <h2>Deposit Phase Proposals</h2>
  {!depositProposals
    ? <p>Loading proposals…</p>
    : depositProposals.length === 0
      ? <p>No proposals in deposit stage.</p>
      : (
        <div className="news-grid">
          {depositProposals.map(p => (
            <div key={p.id} className="news-card deposit-failed">
              <p className="proposal-phase">{p.phase}</p>
              <h3 className="proposal-id">Proposal #{p.id}</h3>
              <p className="proposal-title">{p.title}</p>
              <p className="news-date">
                Deposits end on {new Date(p.deposit_end_time).toLocaleDateString('en-GB')}
              </p>
              <p className="proposal-detail">
                Deposited so far: {p.lunc_deposit.toLocaleString()} LUNC
              </p>
              {p.url && (
                <p className="proposal-url">
                  <a href={p.url} target="_blank" rel="noopener noreferrer">
                    View full proposal
                  </a>
                </p>
              )}
            </div>
          ))}
        </div>
      )
  }
</section>











 </div>
  );
};

export default Home;
